// Ask a device what it is ACTUALLY running.
//
// ── Why this exists, and why nothing else can answer it ────────────────────
// Every other signal in this app answers a different question than the one it
// appears to answer:
//
//   CodeUpdated          stamped by the DEVICE before ESPhttpUpdate runs, so it
//                        records that an update was ATTEMPTED. A device that
//                        rejected the image still has it set.
//   HexFileDateTime      when the FILE was built. Says nothing about any device.
//   OtaUpdateDateTime    when the BACKEND last told a device to update.
//   the retained startup packet's <code_dt> field
//                        looks like a version and is not one — it resolves to
//                        OtaUpdateDateTime, because the firmware declares
//                        `firmware_version` and never writes it.
//                        (OTA_CLAUDE.md §9.2)
//
// So "is this device up to date" has, until now, been answered by comparing two
// timestamps that both come from the wrong events. `VERSION` is the only thing
// that asks the running image.
//
// ⚠️ THE CASE THIS IS REALLY FOR — A SILENT ROLLBACK. If a new C3 image
// crash-loops, the bootloader reverts to the previous slot 60 seconds after
// boot — but the device already published `OTA OK -> booting ota_1` before
// rebooting. So a bad release looks *exactly* like a good one, across the whole
// fleet, and the only way to tell them apart is to ask afterwards
// (OTA_CLAUDE.md §9.4, which asks for this check by name).
//
// ── The reply ──────────────────────────────────────────────────────────────
//     FW 1.0.0 sha=a1b2c3d4e5f60718 idf=v5.3.3 built=Aug  4 2026 14:46:27
//
// ⚠️ `built=` has TWO spaces before a single-digit day — that is C's `__DATE__`.
// Splitting the whole line on whitespace loses the date; everything after
// `built=` is taken as one opaque string here.
//
// ⚠️ ESP8266 DEVICES DO NOT ANSWER THIS. `VERSION` is in the C3 firmware's
// command set; the older fleet has no such command and will simply say nothing,
// which is indistinguishable from being offline. A timeout therefore means
// "no answer", never "old firmware" — and callers must not present it as a
// fault. That is why `ask()` resolves to null rather than throwing.

import {
  publishMessage, subscribeTopic, unsubscribeTopic, addMessageListener, isConnected,
} from '../mqtt/mqttClient';

export const VERSION_COMMAND = 'VERSION';

// The device answers in milliseconds when it answers at all; this is generous
// enough for a congested broker and short enough that a whole fleet can be
// polled without the screen looking hung.
export const VERSION_TIMEOUT_MS = 6000;

/**
 * Parse the `FW …` line.
 *
 * -> { version, sha, idf, built, raw } or null if this is not a version reply.
 *
 * Tolerant of order and of extra tokens: the firmware's format is not frozen,
 * and a parser that insists on the exact shape would start returning null the
 * first time somebody appends a field.
 */
export function parseVersionReply(text) {
  const s = String(text || '').trim();
  if (!/(^|\W)FW\s+\S/i.test(s)) return null;
  const version = (s.match(/\bFW\s+(\S+)/i) || [])[1] || '';
  const sha = (s.match(/\bsha=(\S+)/i) || [])[1] || '';
  const idf = (s.match(/\bidf=(\S+)/i) || [])[1] || '';
  // Everything after `built=` — two spaces and all. Never split on whitespace.
  const built = (s.match(/\bbuilt=(.+)$/i) || [])[1] || '';
  return { version, sha, idf, built: built.trim(), raw: s };
}

// The payload the app puts on the wire. Matching is case- and space-insensitive
// at the device, but the `[uno]` prefix is NOT optional here: without it the
// firmware treats the command as a broadcast and every device listening acts on
// it (OTA_CLAUDE.md §5.1).
export const versionPayload = (uniqueNumber, userId) =>
  `[${uniqueNumber}]${VERSION_COMMAND}#${userId}`;

/**
 * Ask one device for its version.
 *
 * -> the parsed reply, or **null** on timeout. Never throws for "no answer";
 *    see the note above about the ESP8266 fleet.
 *
 * Subscribes BEFORE publishing — the device answers in milliseconds and a
 * subscription taken afterwards loses the reply. Same ordering rule the BLE
 * session exists to enforce.
 */
export function askVersion(uniqueNumber, userId, timeoutMs = VERSION_TIMEOUT_MS) {
  const uno = String(uniqueNumber || '').trim();
  if (!uno || !isConnected()) return Promise.resolve(null);
  const topic = `${uno}/client`;

  return new Promise((resolve) => {
    let done = false;
    let off = null;
    let timer = null;

    const finish = (value) => {
      if (done) return;
      done = true;
      if (timer) clearTimeout(timer);
      if (off) off();
      unsubscribeTopic(topic);
      resolve(value);
    };

    off = addMessageListener((t, payload) => {
      if (t !== topic) return;
      // The status wrapper is `[uno]<body>#<who>`; a VERSION reply arrives on
      // the same topic as switch states and OTA progress, so everything else is
      // ignored rather than mistaken for an answer.
      const body = String(payload || '');
      const inner = body.includes(']') ? body.slice(body.indexOf(']') + 1) : body;
      const parsed = parseVersionReply(inner.split('#')[0]);
      if (parsed) finish(parsed);
    });

    subscribeTopic(topic);
    publishMessage(`${uno}/device`, versionPayload(uno, userId));
    timer = setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * Did the release actually change?
 *
 * -> 'changed' | 'unchanged' | 'unknown'
 *
 * ⚠️ `unchanged` IS THE ALARM, not the boring case: it is what a silent
 * rollback looks like. `unknown` covers the ESP8266 fleet and anything that did
 * not answer, and must never be shown as a failure.
 *
 * ⚠️ Comparing `version` alone is only sound if version.txt is actually bumped
 * per release — OTA_CLAUDE.md §8 says so explicitly. `sha` is the real
 * identifier, so it wins when both sides have one.
 */
/**
 * Is the device running THIS build?
 *
 * -> true | false | null (cannot say)
 *
 * ⚠️ THIS IS THE CHECK THAT ACTUALLY WORKS, and `compareVersions` below is the
 * weaker fallback. Comparing before/after cannot separate a silent rollback
 * from a device that was already on the build you just flashed — that exact
 * case was observed on E072A14582D0, which reported the same
 * `sha=17ad9f1cf7dc0e99` on both sides of a successful update because it was
 * already running that image. Comparing against the FILE's sha has no such
 * ambiguity.
 *
 * The device's `sha=` is the first 8 bytes of the image's `app_elf_sha256`,
 * which `readEspImage` reads straight out of the .bin — verified equal on real
 * hardware.
 *
 * `null` when either side is unknown, and it must be presented as "cannot say".
 * The expected sha is only known for builds published from this browser.
 */
export function matchesBuild(reply, expectedSha8) {
  const got = reply && reply.sha;
  const want = String(expectedSha8 || '').trim();
  if (!got || !want) return null;
  return got.toLowerCase() === want.toLowerCase();
}

export function compareVersions(before, after) {
  if (!before || !after) return 'unknown';
  if (before.sha && after.sha) return before.sha === after.sha ? 'unchanged' : 'changed';
  if (before.version && after.version) {
    return before.version === after.version ? 'unchanged' : 'changed';
  }
  return 'unknown';
}
